// @ts-ignore
import dayjs, { Dayjs, ManipulateType, OpUnitType } from "dayjs";
// @ts-ignore
import utc from "dayjs/plugin/utc";
// @ts-ignore
import timezone from "dayjs/plugin/timezone";

// Initialize plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export class CustomDate {
	private date_instance: Dayjs;

	/**
	 * Initializes the instance. Accepts Date, string, number, or another CustomDate.
	 */
	constructor(date?: Date | string | number | CustomDate | null) {
		if (date instanceof CustomDate) {
			this.date_instance = dayjs(date.to_date());
		} else if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
			// If string with format YYYY-MM-DD force local timezone at T00:00:00
			this.date_instance = dayjs(`${date}T00:00:00`);
		} else {
			// dayjs() with null/undefined defaults to current date
			this.date_instance = dayjs(date || undefined);
		}
	}

	/**
	 * Returns the native JS Date object.
	 */
	to_date(): Date {
		return this.date_instance.toDate();
	}

	/**
	 * Formats the date using dayjs patterns.
	 */
	format(format_pattern: string): string {
		return this.date_instance.format(format_pattern);
	}

	/**
	 * Returns the date in YYYY-MM-DD format.
	 */
	format_year_month_day(): string {
		return this.format("YYYY-MM-DD");
	}

	/**
	 * Returns a specific ISO format with milliseconds and Z.
	 */
	format_to_iso_string(): string {
		return this.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
	}

	/**
	 * Resets the internal date to the start of the day in a specific timezone.
	 */
	set_day_in_different_timezone(timezone_name: string): this {
		const date_string = this.date_instance
			.tz(timezone_name)
			.format("YYYY-MM-DD");
		this.date_instance = dayjs.tz(date_string, timezone_name);
		return this;
	}

	add(value: number, unit: ManipulateType): this {
		this.date_instance = this.date_instance.add(value, unit);
		return this;
	}

	subtract(value: number, unit: ManipulateType): this {
		this.date_instance = this.date_instance.subtract(value, unit);
		return this;
	}

	/**
	 * Returns the absolute difference between dates in the specified unit.
	 */
	diff(date: Date | string | CustomDate, unit: OpUnitType): number {
		const date_to_compare = date instanceof CustomDate ? date.to_date() : date;
		return Math.abs(this.date_instance.diff(date_to_compare, unit));
	}

	start_of(unit: OpUnitType): this {
		this.date_instance = this.date_instance.startOf(unit);
		return this;
	}

	end_of(unit: OpUnitType): this {
		this.date_instance = this.date_instance.endOf(unit);
		return this;
	}

	is_before(
		date: Date | string | CustomDate,
		unit: OpUnitType | null = null
	): boolean {
		const date_to_compare = date instanceof CustomDate ? date.to_date() : date;
		return this.date_instance.isBefore(date_to_compare, unit || undefined);
	}

	is_after(
		date: Date | string | CustomDate,
		unit: OpUnitType | null = null
	): boolean {
		const date_to_compare = date instanceof CustomDate ? date.to_date() : date;
		return this.date_instance.isAfter(date_to_compare, unit || undefined);
	}

	is_same(date: Date | string | CustomDate, unit: OpUnitType): boolean {
		const date_to_compare = date instanceof CustomDate ? date.to_date() : date;
		return this.date_instance.isSame(date_to_compare, unit);
	}

	is_same_or_before(
		date: Date | string | CustomDate,
		unit: OpUnitType | null = null
	): boolean {
		return !this.is_after(date, unit);
	}

	is_same_or_after(
		date: Date | string | CustomDate,
		unit: OpUnitType | null = null
	): boolean {
		return !this.is_before(date, unit);
	}

	is_today(): boolean {
		return this.is_same(new Date(), "day");
	}

	set_minute(value: number): this {
		this.date_instance = this.date_instance.minute(value);
		return this;
	}

	set_hour(value: number): this {
		this.date_instance = this.date_instance.hour(value);
		return this;
	}

	set_month(value: number): this {
		this.date_instance = this.date_instance.month(value);
		return this;
	}

	set_year(value: number): this {
		this.date_instance = this.date_instance.year(value);
		return this;
	}

	set_in_utc(): this {
		this.date_instance = this.date_instance.utc();
		return this;
	}
}
